import type { EngineItem, FeedItemKey } from './index';

/** Returns an estimated row height in px for a content width in px. */
export type ItemHeightEstimator<TItem extends EngineItem = EngineItem> = (
  item: TItem,
  contentWidth: number
) => number;

/** Domain-agnostic fallback row height in px used when no estimator is supplied. */
export const DEFAULT_ITEM_HEIGHT_ESTIMATE_PX = 40;

interface CacheEntry {
  h: number;
  w: number;
  lastUsed: number;
}

interface EstimateEntry {
  h: number;
  item: EngineItem;
  w: number;
}

const WIDTH_MATCH_EPSILON_PX = 0.5;

/** Width-stamped measurement cache; entries are valid only at their measured content width. */
export class HeightCache {
  private entries = new Map<FeedItemKey, CacheEntry>();
  private estimates = new Map<FeedItemKey, EstimateEntry>();
  private sequence = 0;
  private measuredVersion = 0;

  /** Retains the newest extra unmounted rows beyond the loaded key set. */
  constructor(private readonly extraRetained = 300) {}

  /** Stores a positive measured row height and its positive content width in px. */
  setMeasured(key: FeedItemKey, height: number, measuredWidth: number) {
    assertFinitePositive(height, `height for ${key}`);
    assertFinitePositive(measuredWidth, `measuredWidth for ${key}`);
    const previous = this.entries.get(key);
    this.sequence += 1;
    this.entries.set(key, { h: height, w: measuredWidth, lastUsed: this.sequence });
    this.estimates.delete(key);
    if (!previous || previous.h !== height || previous.w !== measuredWidth) {
      this.measuredVersion += 1;
    }
  }

  /** Returns a measured height valid at the supplied content width, or undefined on miss/mismatch. */
  getMeasured(key: FeedItemKey, contentWidth: number): number | undefined {
    const entry = this.entries.get(key);
    if (!entry || Math.abs(entry.w - contentWidth) > WIDTH_MATCH_EPSILON_PX) return undefined;

    this.sequence += 1;
    entry.lastUsed = this.sequence;
    return entry.h;
  }

  /** Drops any cached measurement for the key. */
  delete(key: FeedItemKey) {
    if (this.entries.delete(key)) this.measuredVersion += 1;
    this.deleteEstimate(key);
  }

  /** Drops any cached estimate for the key without discarding measured truth. */
  deleteEstimate(key: FeedItemKey) {
    this.estimates.delete(key);
  }

  /** Monotonic version of layout-relevant measured data. */
  measurementVersion(): number {
    return this.measuredVersion;
  }

  /** Returns a successful estimate only for the same item object and content width. */
  getEstimate<TItem extends EngineItem>(item: TItem, contentWidth: number): number | undefined {
    const entry = this.estimates.get(item.itemKey);
    if (!entry || entry.item !== item || entry.w !== contentWidth) {
      return undefined;
    }
    return entry.h;
  }

  /** Stores a successful estimate for the exact item object and content width. */
  setEstimate<TItem extends EngineItem>(item: TItem, contentWidth: number, height: number) {
    this.estimates.set(item.itemKey, { h: height, item, w: contentWidth });
  }

  /** Current number of retained measurements across content widths. */
  size(): number {
    return this.entries.size;
  }

  /** Removes mismatched-width or excess unmounted entries while preserving mounted keys. */
  prune(input: {
    contentWidth: number;
    loadedKeys: Iterable<FeedItemKey>;
    mountedKeys?: Iterable<FeedItemKey>;
  }) {
    const loaded = new Set(input.loadedKeys);
    const mounted = new Set(input.mountedKeys ?? []);

    for (const [key, entry] of this.entries) {
      if (Math.abs(entry.w - input.contentWidth) > WIDTH_MATCH_EPSILON_PX && !mounted.has(key)) {
        this.entries.delete(key);
      }
    }

    const retainedExtras = [...this.entries]
      .filter(([key, entry]) => Math.abs(entry.w - input.contentWidth) <= WIDTH_MATCH_EPSILON_PX && !loaded.has(key))
      .sort((a, b) => b[1].lastUsed - a[1].lastUsed);

    for (const [key] of retainedExtras.slice(this.extraRetained)) {
      if (!mounted.has(key)) {
        this.entries.delete(key);
      }
    }

    for (const [key, entry] of this.estimates) {
      if (!loaded.has(key) || Math.abs(entry.w - input.contentWidth) > WIDTH_MATCH_EPSILON_PX) {
        this.estimates.delete(key);
      }
    }
  }
}

/** Resolves measured height first, then calls the estimator; result is px and may throw from the estimator. */
export function resolveHeight<TItem extends EngineItem>(
  item: TItem,
  contentWidth: number,
  cache: HeightCache,
  estimateHeight: ItemHeightEstimator<TItem> = defaultEstimateHeight
): number {
  const measured = cache.getMeasured(item.itemKey, contentWidth);
  if (measured !== undefined) return measured;
  const estimated = cache.getEstimate(item, contentWidth);
  if (estimated !== undefined) return estimated;
  const height = estimateHeight(item, contentWidth);
  if (Number.isFinite(height) && height >= 0) {
    cache.setEstimate(item, contentWidth, height);
  }
  return height;
}

/** Returns the fixed fallback height after validating content width is positive px. */
export function defaultEstimateHeight(_item: EngineItem, contentWidth: number): number {
  assertFinitePositive(contentWidth, 'contentWidth');
  return DEFAULT_ITEM_HEIGHT_ESTIMATE_PX;
}

function assertFinitePositive(value: number, name: string) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${name} must be a finite positive number`);
  }
}
